import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Banknote, Landmark, UserRound, ArrowRight } from "lucide-react";

const PRIVATE_RE = /privat|vorgestreckt|selbst bezahlt|selber bezahlt|eigenes geld|inhaber bezahlt/i;
const CASH_RE = /\bbar\b|barzahlung|cash|kasse/i;

const OPTIONS = [
  {
    id: "cash", icon: Banknote, title: "💵 Bar bezahlt",
    desc: "Bucht sofort: Aufwand an Kasse (1000).",
  },
  {
    id: "bank", icon: Landmark, title: "🏦 Bank / Firmenkarte",
    desc: "Keine Buchung nötig — wird beim Bank-Abgleich automatisch zugeordnet und verbucht.",
  },
  {
    id: "private", icon: UserRound, title: "👤 Privat vorgestreckt",
    desc: "Bucht: Aufwand an Kontokorrent Inhaber (2850) — die Firma schuldet dir das Geld.",
  },
];

export const ReceiptPayMethodDialog = ({ receipt, onClose, onBook }) => {
  const navigate = useNavigate();
  const [method, setMethod] = useState("bank");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!receipt) return;
    const hint = `${receipt.user_note || ""} ${receipt.description || ""}`;
    setMethod(PRIVATE_RE.test(receipt.user_note || "") ? "private" : CASH_RE.test(hint) ? "cash" : "bank");
    setNote("");
    setBusy(false);
  }, [receipt]);

  const onNoteChange = (v) => {
    setNote(v);
    if (PRIVATE_RE.test(v)) setMethod("private");
    else if (CASH_RE.test(v)) setMethod("cash");
  };

  const book = async () => {
    setBusy(true);
    await onBook(receipt._id, method, note);
    setBusy(false);
  };

  return (
    <Dialog open={!!receipt} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="receipt-pay-method-dialog">
        <DialogHeader>
          <DialogTitle>Wie wurde dieser Beleg bezahlt?</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setMethod(o.id)}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${method === o.id ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500" : "border-slate-200 hover:bg-slate-50"}`}
              data-testid={`pay-method-${o.id}`}
            >
              <div className="text-sm font-medium">{o.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>
            </button>
          ))}
          <textarea
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder='Notiz (optional) — z.B. "privat bezahlt" oder "bar gekauft" — die KI wählt automatisch die passende Zahlungsart'
            className="w-full rounded-md border border-slate-200 p-2 text-xs min-h-[52px]"
            data-testid="pay-method-note-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pay-method-cancel-btn">Abbrechen</Button>
          {method === "bank" ? (
            <Button
              onClick={() => { onClose(); navigate("/bank-reconciliation"); }}
              data-testid="pay-method-goto-bank-btn"
            >
              Verstanden — zum Bank-Abgleich
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              onClick={book}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="pay-method-book-btn"
            >
              {busy ? "Buche…" : method === "cash" ? "Jetzt buchen (an Kasse 1000)" : "Jetzt buchen (an Kontokorrent 2850)"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
